import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Trash2, UserCheck, Users, Crown, Eye, Pencil, X, Check, GraduationCap, Ticket, Calendar, Mail, KeyRound, Plus, Ban, ShieldOff } from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";

const ROLE_CONFIG = {
  coach:   { label: "Coach",  className: "bg-blue-50 text-blue-700 border-blue-200" },
  student: { label: "Élève",  className: "bg-green-50 text-green-700 border-green-200" },
};

const PLAN_CONFIG = {
  pro:   { label: "Pro",   className: "bg-purple-50 text-purple-700 border-purple-200" },
  basic: { label: "Basic", className: "bg-slate-50 text-slate-600 border-slate-200" },
};

function DetailField({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-400 font-medium">{label}</p>
        <p className="text-sm text-slate-900 break-all">{value ?? '—'}</p>
      </div>
    </div>
  );
}

function SubscriptionSection({ userId, subscription, onSubChange }) {
  const [editingSub, setEditingSub] = useState(false);
  const [savingSub, setSavingSub] = useState(false);
  const [deletingSub, setDeletingSub] = useState(false);
  const [subForm, setSubForm] = useState({
    plan_type: subscription?.plan_type ?? 'basic',
    status: subscription?.status ?? 'active',
    billing_cycle: subscription?.billing_cycle ?? 'monthly',
    current_period_end: subscription?.current_period_end
      ? new Date(subscription.current_period_end).toISOString().slice(0, 10)
      : '',
  });

  const handleSaveSub = async () => {
    setSavingSub(true);
    try {
      const payload = {
        plan_type: subForm.plan_type,
        status: subForm.status,
        billing_cycle: subForm.billing_cycle,
        current_period_end: subForm.current_period_end || null,
      };
      const { data } = await api.put(`/admin/users/${userId}/subscription`, payload);
      onSubChange(data);
      setEditingSub(false);
      toast.success("Abonnement mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSavingSub(false);
    }
  };

  const handleDeleteSub = async () => {
    setDeletingSub(true);
    try {
      await api.delete(`/admin/users/${userId}/subscription`);
      onSubChange(null);
      setEditingSub(false);
      toast.success("Abonnement supprimé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeletingSub(false);
    }
  };

  const STATUS_LABELS = { active: 'Actif', trialing: 'Essai', cancelled: 'Annulé', expired: 'Expiré' };
  const STATUS_CLASSES = {
    active:    'bg-green-50 text-green-700 border-green-200',
    trialing:  'bg-blue-50 text-blue-700 border-blue-200',
    cancelled: 'bg-red-50 text-red-600 border-red-200',
    expired:   'bg-slate-50 text-slate-500 border-slate-200',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Abonnement</h3>
        {!editingSub ? (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs gap-1" onClick={() => setEditingSub(true)}>
            {subscription ? <><Pencil className="w-3 h-3" />Modifier</> : <><Plus className="w-3 h-3" />Attribuer</>}
          </Button>
        ) : (
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingSub(false)}><X className="w-3 h-3" /></Button>
            <Button size="sm" className="h-7 px-2 text-xs gap-1" onClick={handleSaveSub} disabled={savingSub}>
              <Check className="w-3 h-3" />{savingSub ? '...' : 'Sauver'}
            </Button>
          </div>
        )}
      </div>

      {editingSub ? (
        <div className="bg-slate-50 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Plan</label>
              <Select value={subForm.plan_type} onValueChange={v => setSubForm(p => ({ ...p, plan_type: v }))}>
                <SelectTrigger className="h-9 bg-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Statut</label>
              <Select value={subForm.status} onValueChange={v => setSubForm(p => ({ ...p, status: v }))}>
                <SelectTrigger className="h-9 bg-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="trialing">Essai</SelectItem>
                  <SelectItem value="cancelled">Annulé</SelectItem>
                  <SelectItem value="expired">Expiré</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Facturation</label>
              <Select value={subForm.billing_cycle} onValueChange={v => setSubForm(p => ({ ...p, billing_cycle: v }))}>
                <SelectTrigger className="h-9 bg-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensuel</SelectItem>
                  <SelectItem value="annual">Annuel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Fin le <span className="text-slate-400">(vide = illimité)</span></label>
              <Input
                type="date"
                value={subForm.current_period_end}
                onChange={e => setSubForm(p => ({ ...p, current_period_end: e.target.value }))}
                className="h-9 bg-white text-sm"
              />
            </div>
          </div>
          {subscription && (
            <button
              onClick={handleDeleteSub}
              disabled={deletingSub}
              className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 transition-colors mt-1"
            >
              <Ban className="w-3 h-3" />{deletingSub ? 'Suppression...' : 'Supprimer l\'abonnement'}
            </button>
          )}
        </div>
      ) : subscription ? (
        <div className="bg-white border border-slate-100 rounded-xl divide-y divide-slate-50">
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-xs text-slate-500">Plan</span>
            <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${PLAN_CONFIG[subscription.plan_type]?.className ?? ''}`}>
              {subscription.plan_type === 'pro' ? <><Crown className="w-3 h-3 inline mr-0.5" />Pro</> : 'Basic'}
            </span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-xs text-slate-500">Statut</span>
            <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${STATUS_CLASSES[subscription.status] ?? ''}`}>
              {STATUS_LABELS[subscription.status] ?? subscription.status}
            </span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-xs text-slate-500">Facturation</span>
            <span className="text-xs text-slate-700 font-medium">{subscription.billing_cycle === 'annual' ? 'Annuel' : 'Mensuel'}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-xs text-slate-500">Expire le</span>
            <span className="text-xs text-slate-700 font-medium">
              {subscription.current_period_end
                ? new Date(subscription.current_period_end).toLocaleDateString('fr-FR')
                : '—'}
            </span>
          </div>
        </div>
      ) : (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl px-4 py-3 text-center">
          <p className="text-xs text-slate-400">Aucun abonnement actif</p>
        </div>
      )}
    </div>
  );
}

function CoachAssignSection({ userId, currentCoach, onCoachChange }) {
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(currentCoach?.user_id || "__none__");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/users?role=coach")
      .then((r) => setCoaches(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setSelectedCoach(currentCoach?.user_id || "__none__");
  }, [currentCoach]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const coachId = selectedCoach === "__none__" ? null : selectedCoach;
      const { data } = await api.patch(`/admin/users/${userId}/coach`, { coach_id: coachId });
      onCoachChange(data.coach);
      toast.success(coachId ? "Coach assigné" : "Coach retiré");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Coach associé</h3>
      <div className="space-y-3">
        <Select value={selectedCoach} onValueChange={setSelectedCoach}>
          <SelectTrigger className="h-10 bg-white"><SelectValue placeholder="Sélectionner un coach" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Aucun coach</SelectItem>
            {coaches.map((c) => (
              <SelectItem key={c.user_id} value={c.user_id}>{c.name} ({c.email})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-full h-8 text-xs">
          {saving ? "..." : "Mettre à jour le coach"}
        </Button>
      </div>
    </div>
  );
}

function BanSection({ userId, banInfo, onBanChange }) {
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);

  const handleBan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/ban`, {
        reason: reason.trim() || null,
        until: until || null,
      });
      onBanChange(data);
      toast.success("Utilisateur banni");
      setReason("");
      setUntil("");
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const handleUnban = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${userId}/ban`);
      onBanChange({ banned: false, reason: null, banned_at: null, banned_until: null });
      toast.success("Utilisateur débanni");
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (banInfo?.banned) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 text-red-700">
          <Ban className="w-4 h-4" />
          <span className="text-sm font-semibold">Compte banni</span>
        </div>
        {banInfo.reason && <p className="text-xs text-red-600">Raison : {banInfo.reason}</p>}
        {banInfo.banned_until ? (
          <p className="text-xs text-red-500">Jusqu&apos;au {new Date(banInfo.banned_until).toLocaleDateString("fr-FR")}</p>
        ) : (
          <p className="text-xs text-red-500">Bannissement permanent</p>
        )}
        <Button size="sm" variant="outline" onClick={handleUnban} disabled={busy} className="rounded-full gap-1 text-green-700 border-green-200">
          <ShieldOff className="w-3.5 h-3.5" /> Débannir
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Bannir l&apos;utilisateur</h3>
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Raison (optionnel)" className="h-9 bg-white text-sm" />
      <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="h-9 bg-white text-sm" placeholder="Date de fin" />
      <p className="text-[10px] text-slate-400">Laissez la date vide pour un bannissement permanent</p>
      <Button size="sm" variant="outline" onClick={handleBan} disabled={busy} className="rounded-full gap-1 text-red-600 border-red-200 hover:bg-red-50">
        <Ban className="w-3.5 h-3.5" /> Bannir
      </Button>
    </div>
  );
}

function UserPanel({ userId, onClose, onUpdated, onDeleted }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    setEditing(false);
    api.get(`/admin/users/${userId}`)
      .then(r => { setProfile(r.data); setForm({ name: r.data.name, email: r.data.email, password: '' }); })
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email };
      if (form.password.trim()) payload.password = form.password;
      const { data } = await api.patch(`/admin/users/${userId}`, payload);
      setProfile(prev => ({ ...prev, ...data }));
      onUpdated(data);
      setEditing(false);
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const roleCfg = ROLE_CONFIG[profile?.role] ?? ROLE_CONFIG.student;

  return (
    <SheetContent className="w-full sm:max-w-md overflow-y-auto p-0">
      <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
        <SheetTitle className="flex items-center justify-between">
          <span>Profil utilisateur</span>
          {!editing ? (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="gap-1.5">
              <Pencil className="w-3.5 h-3.5" /> Modifier
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                <X className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
                <Check className="w-3.5 h-3.5" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </div>
          )}
        </SheetTitle>
      </SheetHeader>

      {loading ? (
        <div className="px-6 py-6 space-y-4">
          <div className="flex items-center gap-4"><Skeleton className="w-16 h-16 rounded-full" /><div className="space-y-2"><Skeleton className="h-4 w-32" /><Skeleton className="h-3 w-48" /></div></div>
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}
        </div>
      ) : profile && (
        <div className="px-6 py-5 space-y-6">
          {/* Avatar + role */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              {profile.name?.[0]?.toUpperCase() ?? '?'}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-lg font-bold text-slate-900 truncate">{profile.name}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${roleCfg.className}`}>{roleCfg.label}</span>
              </div>
              <p className="text-sm text-slate-400 truncate">{profile.email}</p>
              {profile.role === 'coach' && profile.subscription && (
                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium mt-1 inline-flex items-center gap-1 ${PLAN_CONFIG[profile.subscription.plan_type]?.className ?? ''}`}>
                  <Crown className="w-3 h-3" /> Plan {profile.subscription.plan_type}
                </span>
              )}
            </div>
          </div>

          {/* Edit form */}
          {editing ? (
            <div className="space-y-3 bg-slate-50 rounded-xl p-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Nom</label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="h-9 bg-white" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Email</label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="h-9 bg-white" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block flex items-center gap-1">
                  <KeyRound className="w-3 h-3" /> Nouveau mot de passe <span className="text-slate-400">(laisser vide = inchangé)</span>
                </label>
                <Input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="8 caractères min." className="h-9 bg-white" />
              </div>
            </div>
          ) : (
            <div className="bg-slate-50 rounded-xl p-4">
              <DetailField label="Nom" value={profile.name} icon={UserCheck} />
              <DetailField label="Email" value={profile.email} icon={Mail} />
              <DetailField label="ID" value={profile.user_id} icon={Search} />
              <DetailField label="Inscrit le" value={profile.created_at ? new Date(profile.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : null} icon={Calendar} />
              {profile.role === 'coach' && profile.coach_code && (
                <DetailField label="Code coach" value={profile.coach_code} icon={Crown} />
              )}
            </div>
          )}

          {/* Coach: Abonnement éditable + Élèves */}
          {profile.role === 'coach' && (
            <div className="space-y-5">
              <SubscriptionSection
                userId={userId}
                subscription={profile.subscription}
                onSubChange={sub => setProfile(prev => ({ ...prev, subscription: sub }))}
              />
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Élèves <span className="text-slate-400 font-normal">({profile.students_count ?? 0})</span>
                </h3>
                {profile.students?.length > 0 ? (
                  <div className="bg-white border border-slate-100 rounded-xl divide-y divide-slate-50">
                    {profile.students.map(s => (
                      <div key={s.user_id} className="flex items-center gap-3 px-4 py-2.5">
                        <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-xs font-bold flex-shrink-0">
                          {s.name?.[0]?.toUpperCase() ?? '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-900 truncate">{s.name}</p>
                          <p className="text-xs text-slate-400 truncate">{s.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3">Aucun élève</p>
                )}
              </div>
            </div>
          )}

          {profile.role === "student" && (
            <CoachAssignSection
              userId={userId}
              currentCoach={profile.coach_id ? { user_id: profile.coach_id, ...profile.coach } : null}
              onCoachChange={(coach) => setProfile((prev) => ({
                ...prev,
                coach,
                coach_id: coach?.user_id ?? null,
              }))}
            />
          )}

          <BanSection
            userId={userId}
            banInfo={{
              banned: profile.banned,
              reason: profile.reason,
              banned_at: profile.banned_at,
              banned_until: profile.banned_until,
            }}
            onBanChange={(info) => {
              setProfile((prev) => ({ ...prev, ...info }));
              onUpdated({ user_id: userId, banned: info.banned });
            }}
          />

          {/* Ticket count */}
          <div className="flex items-center justify-between bg-white border border-slate-100 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Ticket className="w-4 h-4 text-orange-500" />
              Tickets support
            </div>
            <span className="text-sm font-bold text-slate-900">{profile.tickets_count ?? 0}</span>
          </div>

          {/* Delete */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50 gap-2">
                <Trash2 className="w-4 h-4" /> Supprimer cet utilisateur
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
                <AlertDialogDescription>
                  <strong>{profile.name}</strong> ({profile.email}) sera définitivement supprimé. Cette action est irréversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDeleted(profile.user_id)} className="bg-red-600 hover:bg-red-700 text-white">
                  Supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </SheetContent>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (roleFilter) params.set('role', roleFilter);
    if (search.trim()) params.set('search', search.trim());
    api.get(`/admin/users?${params}`)
      .then(r => setUsers(r.data))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [roleFilter, search]);

  useEffect(() => { load(); }, [load]);

  const handleUpdated = (updated) => {
    setUsers(prev => prev.map(u => u.user_id === updated.user_id ? { ...u, ...updated } : u));
  };

  const handleDeleted = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("Utilisateur supprimé");
      setUsers(prev => prev.filter(u => u.user_id !== userId));
      setSelectedUserId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const filters = [
    { value: "",        label: "Tous",   icon: Users },
    { value: "coach",   label: "Coachs", icon: UserCheck },
    { value: "student", label: "Élèves", icon: GraduationCap },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Utilisateurs"
        subtitle={`${users.length} utilisateur${users.length > 1 ? "s" : ""} · gestion, abonnements, bannissements`}
      />

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher..."
            className="pl-9 h-10 bg-white border-slate-200"
          />
        </div>
        <div className="flex gap-2">
          {filters.map(f => (
            <button
              key={f.value}
              onClick={() => setRoleFilter(f.value)}
              className={`flex items-center gap-1.5 px-3 h-10 rounded-full text-sm font-medium transition-colors border ${
                roleFilter === f.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              <f.icon className="w-3.5 h-3.5" />
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="divide-y divide-slate-100">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-6 py-4">
                  <Skeleton className="w-9 h-9 rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-36" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="h-6 w-16 rounded-full" />
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-16 text-slate-400 text-sm">Aucun utilisateur trouvé</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {users.map(u => {
                const roleCfg = ROLE_CONFIG[u.role] ?? ROLE_CONFIG.student;
                return (
                  <div
                    key={u.user_id}
                    className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50 transition-colors cursor-pointer group"
                    onClick={() => setSelectedUserId(u.user_id)}
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                      {u.name?.[0]?.toUpperCase() ?? '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900 truncate">{u.name}</p>
                        {u.banned && (
                          <span className="text-xs px-2 py-0.5 rounded-full border font-medium bg-red-50 text-red-600 border-red-200">
                            Banni
                          </span>
                        )}
                        {u.role === 'coach' && u.subscription && (
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${PLAN_CONFIG[u.subscription.plan_type]?.className ?? ''}`}>
                            {u.subscription.plan_type === 'pro' ? <><Crown className="w-3 h-3 inline mr-0.5" />Pro</> : 'Basic'}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 truncate">{u.email}</p>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold flex-shrink-0 ${roleCfg.className}`}>
                      {roleCfg.label}
                    </span>
                    <p className="text-xs text-slate-400 flex-shrink-0 hidden md:block">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '—'}
                    </p>
                    <Eye className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile panel */}
      <Sheet open={!!selectedUserId} onOpenChange={open => { if (!open) setSelectedUserId(null); }}>
        {selectedUserId && (
          <UserPanel
            userId={selectedUserId}
            onClose={() => setSelectedUserId(null)}
            onUpdated={handleUpdated}
            onDeleted={handleDeleted}
          />
        )}
      </Sheet>
    </div>
  );
}
